import * as Yup from 'yup'
import { Form, Row, Collapse, Accordion, Table } from 'react-bootstrap'
import { CustomAccordionButton, CustomFormControl, CustomLink } from '../../../../StyledComponents';
import { useState } from 'react';
import { characterRestrictionInDocNamesRegExp } from '../ValidationRules';
import { truncate } from 'fs';

const applicationPermittedExtensions = ['.pdf','.jpg', '.png']
const applicationPermittedMimeTypes = ['image/jpeg', 'image/png', 'application/pdf']

function getFileSizeAsString(totalBytes: number): string {
    const Gigabyte = 1073741824
    const Megabyte = 1048576
    const Kilobyte = 1024

    if (totalBytes <= 0) return "0 Bytes"

    if (totalBytes >= Gigabyte) {
        return (totalBytes / Gigabyte).toFixed(2) + " GB"
    } else if (totalBytes >= Megabyte) {
        return (totalBytes / Megabyte).toFixed(2) + " MB"
    } else if (totalBytes >= Kilobyte) {
        return (totalBytes / Kilobyte).toFixed(2) + " kB"
    } else {
        return totalBytes + " Bytes"
    }
}

const checkRequiredDokument = (settings) => (Yup.mixed()
    .required(`Dokument ist erforderlich und darf nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`)
    .test("FileSize", `Dokument darf nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`, (file) => file && file.size < settings.applicationFileSizeLimit)
    .test("NullByteTest", "Die Datei darf nicht leer sein.", (file) => {
        if (file) {
            if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
            file.isValid = file.size > 0;
            if (!file.isValid) file.errMessage = "Die Datei darf nicht leer sein.";
            return file.isValid;
        }
    })
    .test("FileFormat", `Folgende Dateiformate sind erlaubt: ${applicationPermittedExtensions.join(" ")}`, (file) => {
        if (file) {
            if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
            file.isValid = applicationPermittedMimeTypes.includes(file.type);
            if (!file.isValid) file.errMessage = `Folgende Dateiformate sind erlaubt: ${applicationPermittedExtensions.join(" ")}`;
            return file.isValid;
        }
    })
    .when("settings.isUnicode", {
        is: () => !settings.isUnicode,
        then: Yup.mixed()
            .test("FileNameTest",
                `Ungültiger Dokumentenname`,
                (file) => {
                    if (file) {
                        if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                        file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                        if (!file.isValid) file.errMessage = "Ungültiger Dokumentenname";
                        return file.isValid;
                    }
                })
    }));

export const Schema = settings => (Yup.object().shape({
    letterFile: checkRequiredDokument(settings),
    cvFile: checkRequiredDokument(settings),
    schoolDiplomaFile: checkRequiredDokument(settings),
    additionalFiles: Yup.array()
        .nullable()
        .test("FileSize",
            `Die Unterlagen dürfen höchstens ${settings.applicationFileNumberLimit} Dateien und zusammen nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`,
            (files) => {
                if (files && files.length > 0) {
                    let totalSize: number = files.reduce((accumulator, currentFile) => accumulator + currentFile.size, 0);
                    return totalSize <= settings.applicationFileSizeLimit && files.length <= settings.applicationFileNumberLimit;
                } else {
                    return true;
                }
            })
        .test("FileFormat",
            `Folgende Dateiformate sind erlaubt: ${applicationPermittedExtensions.join(" ")}`,
            (files) => {
                if (files && files.length > 0) {
                    let allValid = true;
                    for (let file of files) {
                        if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                        if (!applicationPermittedMimeTypes.includes(file.type)) {
                            file.isValid = false;
                            file.errMessage = `Folgende Dateiformate sind erlaubt: ${applicationPermittedExtensions.join(" ")}`;
                            allValid = false;
                        }
                    }
                    return allValid;
                } else {
                    return true;
                }
            })
        .when("settings.isUnicode", {
            is: () => !settings.isUnicode,
            then: Yup.array().nullable()
                .test("FileNameTest",
                    `Ungültiger Dokumentenname`,
                    (files) => {
                        if (files && files.length > 0) {
                            let allValid = true;
                            for (let file of files) {
                                if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                if (!file.name.match(characterRestrictionInDocNamesRegExp)) {
                                    file.isValid = false;
                                    file.errMessage = `Ungültiger Dokumentenname`;
                                    allValid = false;
                                }
                            }
                            return allValid;
                        } else {
                            return true;
                        }
                    })
        }),
    }));

class FileExt extends File {
    isValid: boolean = true;
    errMessage: string = "";
}

export const DocumentsDiplomaSubForm = ({ form: { values, setFieldTouched, setFieldValue, handleBlur, handleChange, touched, errors }, ...props }) => {
    const { colors, settings } = props;
    const [cvDocument, setCvDocument] = useState<FileExt>();
    const [letterDocument, setLetterDocument] = useState<FileExt>();
    const [schoolDiplomaDocument, setSchoolDiplomaDocument] = useState<FileExt>();
    const [additionalDocuments, setAdditionalDocuments] = useState<FileExt[]>([]);

    function getTotalSizeOfAdditionalDocs(): number {
        if (!additionalDocuments || additionalDocuments?.length === 0) return 0

        let totalSize: number = 0
        for (let i = 0; i < additionalDocuments.length; i++) {
            const element = additionalDocuments[i];
            totalSize += element.size
        }
        return totalSize
    }

    function getTotalSizeOfDocs(): number {
        let totalSizeOfAddititionDocs = getTotalSizeOfAdditionalDocs();
        let cvDocumentSize = cvDocument ? cvDocument.size : 0;
        let letterDocumentSize = letterDocument ? letterDocument.size : 0;
        let schoolDiplomaDocumentSize = schoolDiplomaDocument ? schoolDiplomaDocument.size : 0;
        return totalSizeOfAddititionDocs + cvDocumentSize + letterDocumentSize + schoolDiplomaDocumentSize;
    }

    function fileNameExists(files1: FileExt[], files2: FileExt[]): boolean {
        if (files1 && files1.length > 0 && files2 && files2.length) {
            for (let i = 0; i < files1.length; i++) {
                for (let j = 0; j < files2.length; j++) {
                    if (files1[i].name === files2[j].name) {
                        files1[i] = files2[j];
                        return true;
                    }
                    if (files2[j].name === cvDocument?.name || files2[j].name===letterDocument?.name || files2[j].name===schoolDiplomaDocument?.name){
                        return true
                    }
                }
            }
            return false;
        } else  if (files2 && files2.length>0 )
            {
                for (let j = 0; j < files2.length; j++) {
                    if (files2[j].name === cvDocument?.name || files2[j].name===letterDocument?.name || files2[j].name===schoolDiplomaDocument?.name){
                        return true
                    }
            }
            return false;
        } else {
            return false;
        }
    }

    function cvfileNameExists(files: FileExt[],file:FileExt): boolean {
        if (files && files.length > 0 && file ) {
            for (let i = 0; i < files.length; i++) {
                    if (files[i].name === file.name){
                        return true
                    }
                }
            return false;
        } else if (file.name===letterDocument?.name || file.name===schoolDiplomaDocument?.name) {
             return true
        } else {
            return false;
        }
    }
    
    function letterfileNameExists(files: FileExt[],file:FileExt): boolean {
        if (files && files.length > 0 && file ) {
            for (let i = 0; i < files.length; i++) {
                    if (files[i].name === file.name){
                        return true
                    }
                }
            return false;
        } else if (file.name===cvDocument?.name || file.name===schoolDiplomaDocument?.name) {
             return true
        } else {
            return false;
        }
    }

    function schoolDiplomafileNameExists(files: FileExt[],file:FileExt): boolean {
        if (files && files.length > 0 && file ) {
            for (let i = 0; i < files.length; i++) {
                    if (files[i].name === file.name){
                        return true
                    }
                }
            return false;
        } else if (file.name===cvDocument?.name || file.name===letterDocument?.name) {
             return true
        } else {
            return false;
        }
    }

   

    return (
        <Row>
            <Form.Group className="mb-3" controlId="formLetterDoc">
                <Form.Label>Anschreiben* {letterDocument && "(" + getFileSizeAsString(letterDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="letterFile"
                    onChange={(event) => {
                        if (event.target.files[0]&& !letterfileNameExists(additionalDocuments,event.target.files[0])) {
                            setLetterDocument(event.target.files[0]);
                            setFieldTouched("letterFile");
                            setFieldValue("letterFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.letterFile && !errors.letterFile}
                    isInvalid={touched.letterFile && errors.letterFile}
                    accept={applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.letterFile}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: touched.letterFile && errors.letterFile ? "none" : "flex" }}>
                    zulässige Formate: {applicationPermittedExtensions.join(",")}
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCvDoc">
                <Form.Label>Lebenslauf* {cvDocument && "(" + getFileSizeAsString(cvDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="cvFile"
                    onChange={(event) => {
                        if (event.target.files[0] && !cvfileNameExists(additionalDocuments,event.target.files[0]))
                        {
                            setFieldTouched("cvFile");
                            setFieldValue("cvFile", event.target.files[0] || undefined);
                            setCvDocument(event.target.files[0]);
                        }
                    }}
                    isValid={touched.cvFile && !errors.cvFile}
                    isInvalid={touched.cvFile && errors.cvFile}
                    accept={applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.cvFile}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: touched.cvFile && errors.cvFile ? "none" : "flex" }}>
                    zulässige Formate: {applicationPermittedExtensions.join(",")}
                </Form.Text>
            </Form.Group>



            <Form.Group className="mb-3" controlId="formSchoolDiplomaDoc">
                <Form.Label>Schulzeugnisse und Praktika-Nachweise* {schoolDiplomaDocument && "(" + getFileSizeAsString(schoolDiplomaDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="schoolDiplomaFile"
                    onChange={(event) => {
                        if (event.target.files[0] && !schoolDiplomafileNameExists(additionalDocuments,event.target.files[0])) {
                            setSchoolDiplomaDocument(event.target.files[0]);
                            setFieldTouched("schoolDiplomaFile");
                            setFieldValue("schoolDiplomaFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.schoolDiplomaFile && !errors.schoolDiplomaFile}
                    isInvalid={touched.schoolDiplomaFile && errors.schoolDiplomaFile}
                    accept={applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.schoolDiplomaFile}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: touched.schoolDiplomaFile && errors.schoolDiplomaFile ? "none" : "flex" }}>
                    zulässige Formate: {applicationPermittedExtensions.join(",")}
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formAdditionalFiles">
                <Form.Label>Weitere Unterlagen (optional) {additionalDocuments && getTotalSizeOfAdditionalDocs() > 0 && "(" + getFileSizeAsString(getTotalSizeOfAdditionalDocs()) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    multiple
                    name="additionalFiles"
                    onChange={(event) => {

                        if (event.target.files?.length) {
                            let files: FileExt[] = [];
                            if (additionalDocuments.length > 0) {
                                files = additionalDocuments.slice();
                            }
                            if (!fileNameExists(files, Array.from<FileExt>(event.target.files))) {
                            files.push(...Array.from<FileExt>(event.target.files));
                            }
                            setAdditionalDocuments(files);
                            setFieldTouched("additionalFiles");
                            setFieldValue("additionalFiles", files || undefined);
                        }

                    }}
                    isValid={touched.additionalFiles && !errors.additionalFiles}
                    isInvalid={errors.additionalFiles}
                    accept={applicationPermittedExtensions} />
                <Form.Control.Feedback type="invalid">
                    {errors.additionalFiles}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: errors.additionalFiles ? "none" : "flex" }}>
                    Mehrfachauswahl möglich: Maximal {settings.applicationFileNumberLimit} Dateien (Lebenslauf + {settings.applicationFileNumberLimit-1} weitere Dateien) mit insgesamt {getFileSizeAsString(settings.applicationFileSizeLimit)}
                </Form.Text>
            </Form.Group>

            <div style={{ marginBottom: "16px" }}>
                <Collapse in={getTotalSizeOfDocs() > 0}>
                    <Accordion defaultActiveKey="0">
                        <Accordion.Item eventKey="0">
                            <CustomAccordionButton colors={colors}>Ausgewählte Dateien ({getFileSizeAsString(getTotalSizeOfDocs())})</CustomAccordionButton>
                            <Accordion.Body>
                                <Table>
                                    <tbody>
                                        {cvDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={cvDocument.isValid ? "" : "text-danger"}><strong>{cvDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(cvDocument.size)}</td>
                                                <td>Lebenslauf</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCvDocument(undefined);
                                                        setFieldTouched("cvFile");
                                                        setFieldValue("cvFile", undefined);

                                                        let inputElement = document.getElementById("formCvDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={cvDocument.errMessage} style={{ visibility: cvDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {letterDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={letterDocument.isValid ? "" : "text-danger"}><strong>{letterDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(letterDocument.size)}</td>
                                                <td>Anschreiben</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setLetterDocument(undefined);
                                                        setFieldTouched("letterFile");
                                                        setFieldValue("letterFile", undefined);

                                                        let inputElement = document.getElementById("formLetterDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={letterDocument.errMessage} style={{ visibility: letterDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {schoolDiplomaDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={schoolDiplomaDocument.isValid ? "" : "text-danger"}><strong>{schoolDiplomaDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(schoolDiplomaDocument.size)}</td>
                                                <td>Schulzeugnisse und Praktika-Nachweise</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setSchoolDiplomaDocument(undefined);
                                                        setFieldTouched("schoolDiplomaFile");
                                                        setFieldValue("schoolDiplomaFile", undefined);

                                                        let inputElement = document.getElementById("formSchoolDiplomaDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={schoolDiplomaDocument.errMessage} style={{ visibility: schoolDiplomaDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {additionalDocuments && Array.from(additionalDocuments).map((file: FileExt) =>
                                            <tr key={file.name}>
                                                <td style={{ wordBreak: "break-all" }} className={file.isValid === undefined || file.isValid ? "" : "text-danger"}>{file.name}</td>
                                                <td>{getFileSizeAsString(file.size)}</td>
                                                <td>Weitere Unterlagen</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        let pos = -1;
                                                        for (let i = 0; i < additionalDocuments.length; i++) {
                                                            const f: FileExt = additionalDocuments[i]
                                                            if (f.name === file.name) {
                                                                pos = i;
                                                                break;
                                                            }
                                                        }

                                                        if (pos > -1) {
                                                            let files: FileExt[] = [];
                                                            files = additionalDocuments.slice();
                                                            files.splice(pos, 1);

                                                            setAdditionalDocuments(files);
                                                            setFieldValue("additionalFiles", files || undefined);

                                                            if (files.length === 0) {
                                                                let inputElement = document.getElementById("formAdditionalFiles") as HTMLInputElement;
                                                                inputElement.value = "";
                                                            }
                                                        }
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={file.errMessage} style={{ visibility: file.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Collapse>
            </div>
        </Row>
    );
};
